import Dexie, { type DBCore, type DBCoreMutateRequest, type DBCoreTable, type EntityTable, type Middleware, type Transaction } from 'dexie';
import type {
  Account,
  AccountEvent,
  Attachment,
  BackupLogEntry,
  BankRow,
  BankStatement,
  Company,
  CompanySettings,
  DeviceRegistration,
  DriveState,
  FiscalYear,
  InboxItem,
  MerchantRule,
  PaymentSource,
  PeriodLock,
  Settings,
  UploadQueueItem,
  Verifikation,
  ArchiveFile,
} from '@/domain/types';
// The upgrade step reuses the domain's pure default builders so migrated rows
// are identical to freshly created ones. Only leaf modules (no `db` import)
// are used here, keeping the dependency direction domain -> db.
import { emptyDriveState } from '@/domain/companies/defaults';
import { defaultCompanySettings } from '@/domain/company-settings/defaults';
import { defaultPaymentSources } from '@/domain/payment-sources/rules';
import { withSettingsDefaults } from '@/domain/settings/defaults';

/** Tables that only ever accept `add`. Enforced below at the DBCore layer. */
export const APPEND_ONLY_TABLES: ReadonlySet<string> = new Set(['verifikationer', 'accountEvents', 'periodLocks']);

export class AppendOnlyViolation extends Error {
  constructor(table: string, op: string) {
    super(`Table "${table}" is append-only; "${op}" is not allowed`);
    this.name = 'AppendOnlyViolation';
  }
}

/**
 * DBCore middleware that rejects every mutation except `add` on append-only
 * tables. Table.update/put/delete/clear all end up as put/delete/deleteRange
 * here, so this covers all Dexie APIs.
 */
export const appendOnlyMiddleware: Middleware<DBCore> = {
  stack: 'dbcore',
  name: 'appendOnly',
  create(down: DBCore): DBCore {
    return {
      ...down,
      table(name: string): DBCoreTable {
        const table = down.table(name);
        if (!APPEND_ONLY_TABLES.has(name)) return table;
        return {
          ...table,
          mutate(req: DBCoreMutateRequest) {
            if (req.type !== 'add') {
              return Promise.reject(new AppendOnlyViolation(name, req.type));
            }
            return table.mutate(req);
          },
        };
      },
    };
  },
};

/**
 * v1 -> v2 data migration. Exported so tests can run it against a v1-shaped
 * database. Idempotent: rows that already exist are left alone.
 */
/** Backfill `hasLocalBlob` for attachments stored before the flag existed. */
export async function upgradeToV4(tx: Transaction): Promise<void> {
  await tx
    .table<Attachment, string>('attachments')
    .toCollection()
    .modify((a) => {
      a.hasLocalBlob = a.blob ? 1 : 0;
    });
}

export async function upgradeToV2(tx: Transaction): Promise<void> {
  const now = new Date().toISOString();
  const settings = tx.table<Settings, string>('settings');
  const companies = tx.table<Company, string>('companies');
  const companySettings = tx.table<CompanySettings, string>('companySettings');
  const paymentSources = tx.table<PaymentSource, string>('paymentSources');
  const driveState = tx.table<DriveState, string>('driveState');

  const allCompanies = await companies.toArray();

  const rawSettings = await settings.get('settings');
  if (rawSettings) {
    const next = withSettingsDefaults(rawSettings);
    // An existing installation with a company has already been "onboarded".
    if (!(rawSettings as Partial<Settings>).onboarding && next.activeCompanyId) {
      next.onboarding = { step: 5, companyId: next.activeCompanyId };
    }
    await settings.put(next);
  }

  for (const company of allCompanies) {
    if (!(await companySettings.get(company.id))) {
      const existingSources = await paymentSources.where('companyId').equals(company.id).toArray();
      const sources = existingSources.length ? existingSources : defaultPaymentSources(company, now);
      if (!existingSources.length) await paymentSources.bulkAdd(sources);
      const bank = sources.find((s) => s.kind === 'bank' && s.active) ?? null;
      await companySettings.add(
        defaultCompanySettings(company, now, {
          defaultPaymentSourceId: bank?.id ?? null,
          onboarding: { completedAt: now, driveSkippedAt: null, aiSkippedAt: null },
        }),
      );
    }
    if (!(await driveState.get(company.id))) {
      await driveState.add(emptyDriveState(company.id));
    }
  }
}

export class BookkeepingDB extends Dexie {
  companies!: EntityTable<Company, 'id'>;
  fiscalYears!: EntityTable<FiscalYear, 'id'>;
  accounts!: EntityTable<Account, 'id'>;
  accountEvents!: EntityTable<AccountEvent, 'id'>;
  verifikationer!: EntityTable<Verifikation, 'id'>;
  settings!: EntityTable<Settings, 'id'>;
  companySettings!: EntityTable<CompanySettings, 'id'>;
  paymentSources!: EntityTable<PaymentSource, 'id'>;
  inboxItems!: EntityTable<InboxItem, 'id'>;
  attachments!: EntityTable<Attachment, 'id'>;
  bankStatements!: EntityTable<BankStatement, 'id'>;
  bankRows!: EntityTable<BankRow, 'id'>;
  merchantRules!: EntityTable<MerchantRule, 'id'>;
  periodLocks!: EntityTable<PeriodLock, 'id'>;
  driveState!: EntityTable<DriveState, 'id'>;
  backupLog!: EntityTable<BackupLogEntry, 'id'>;
  uploadQueue!: EntityTable<UploadQueueItem, 'id'>;
  devices!: EntityTable<DeviceRegistration, 'id'>;
  archiveFiles!: EntityTable<ArchiveFile, 'id'>;

  constructor(name = 'bokforing') {
    super(name);
    // Never edit an existing version block; add a new version with an upgrade step.
    this.version(1).stores({
      companies: 'id, name',
      fiscalYears: 'id, companyId, [companyId+startDate], status',
      accounts: 'id, [companyId+number], companyId, active',
      accountEvents: 'id, companyId, [companyId+accountNumber], fiscalYearId, at',
      verifikationer: 'id, companyId, fiscalYearId, [fiscalYearId+series+number], [companyId+date], correctsId',
      settings: 'id',
    });
    this.version(2)
      .stores({
        companies: 'id, name',
        fiscalYears: 'id, companyId, [companyId+startDate], status',
        accounts: 'id, [companyId+number], companyId, active',
        accountEvents: 'id, companyId, [companyId+accountNumber], fiscalYearId, at',
        verifikationer:
          'id, companyId, fiscalYearId, [fiscalYearId+series+number], [companyId+date], correctsId, meta.inboxItemId, meta.periodKey',
        settings: 'id',
        companySettings: 'id, companyId',
        paymentSources: 'id, companyId, [companyId+kind], active',
        inboxItems: 'id, companyId, [companyId+status], [companyId+tier], driveFileId, bankRowId, verifikationId, createdAt',
        attachments: 'id, companyId, driveFileId, sha256',
        bankStatements: 'id, companyId, paymentSourceId, importedAt',
        bankRows: 'id, &fingerprint, companyId, [companyId+status], [paymentSourceId+bookingDate], statementId, bankId',
        merchantRules: 'id, [companyId+merchantKey], companyId',
        periodLocks: 'id, companyId, [companyId+periodKey], fiscalYearId, lockedThrough',
        driveState: 'id, companyId',
        backupLog: 'id, companyId, at',
        uploadQueue: 'id, status, createdAt',
      })
      .upgrade(upgradeToV2);
    // v3 only adds an index: `[companyId+name]` lets the Händelser search read
    // attachment file names straight off the index (`attachmentNames`) without
    // loading a single receipt blob. No data change, so no upgrade step.
    this.version(3).stores({
      attachments: 'id, companyId, driveFileId, sha256, [companyId+name]',
    });
    // v4 indexes `hasLocalBlob` so the receipts a restore still has to fetch
    // can be listed from the index instead of by reading every attachment
    // (blobs and all) to look at `blob`.
    this.version(4)
      .stores({
        attachments: 'id, companyId, driveFileId, sha256, [companyId+name], [companyId+hasLocalBlob]',
      })
      .upgrade(upgradeToV4);
    // v5 records a storage move explicitly. Switching backend used to repoint
    // every local reference and drop the old folder ids in one go, so an
    // interrupted move left a half-remapped database with no way to tell what
    // had landed. The two tables hold the transition instead: nothing local
    // changes until the copy is verified, and both ids exist until then.
    // New tables only, so no upgrade step.
    this.version(5).stores({
      storageMigrations: 'id, status, startedAt',
      storageMoves: 'id, migrationId, [migrationId+oldId], oldId',
    });
    // v6 names the devices that write to one company's bookkeeping, so the
    // `deviceId` a verifikation carries resolves to something a person
    // recognises — on this machine and, through the backup, on the next one.
    // A new table only, so no upgrade step: the identity itself is minted
    // lazily by `ensureDeviceIdentity`, not by a migration.
    this.version(6).stores({
      devices: 'id, companyId, deviceId, [companyId+deviceId]',
    });
    // v7 indexes the handover batch an inbox item arrived in, so "det jag
    // just lade till" is a filter instead of a scan. The batch is provenance
    // only; matching and dates never depend on it. The other fields the pile
    // workflow adds to an item (`extraction`, `classification`, `decided`,
    // `rejectedMatches`, `mergedIntoItemId`, `extraAttachmentIds`,
    // `verifikationIds`, `proposal.allocations`) are optional and unindexed,
    // so no upgrade step: an older row simply has none of them.
    this.version(7).stores({
      inboxItems: 'id, companyId, [companyId+status], [companyId+tier], [companyId+batchId], driveFileId, bankRowId, verifikationId, createdAt',
    });
    this.version(8).stores({ archiveFiles: 'id, companyId, [companyId+path], statementId' });
    // v9 drops the storage-move tables. Connecting a backend no longer copies
    // the archive out of the old one: the local database is the master copy, so
    // a new store is seeded by rebuilding the tree and writing a full snapshot,
    // and "Kontrollera arkivet" re-uploads the receipts from their local bytes.
    // With nothing to repoint there is no transition to record.
    this.version(9).stores({ storageMigrations: null, storageMoves: null });
    this.use(appendOnlyMiddleware);
  }
}

export const db = new BookkeepingDB();
